import { useAuthStore } from '@/store/auth';
import { FinanceAttributionLinkForm } from './FinanceAttributionLinkForm';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Store, Loader2 } from 'lucide-react';
import { attributionInstallationApi, attributionLinkApi } from '@/lib/finance-channel-api';

// Panel pojedynczego sklepu w Finanse -> Ustawienia -> Pomiar wejść w sklepie.
// Jedna decyzja operatora: włącz/wyłącz. PREPARE i ENABLE wykonują się razem,
// reszta (odczyty, powiązania zamówień) działa automatycznie raz dziennie.
export function FinanceAttributionStorePanel({ storeId, storeName, brandId, kreatorHref }: { storeId: string; storeName: string; brandId: string; kreatorHref?: string }) {
  const tenantId = useAuthStore(state => state.tenant?.id);
  const userId = useAuthStore(state => state.user?.id);
  const cache = useQueryClient();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const key = ['attribution-installation', tenantId, userId, storeId, brandId];
  const status = useQuery({ queryKey: key, queryFn: async () => (await attributionInstallationApi.read(storeId, brandId)).data, retry: false });

  const enable = useMutation({
    mutationFn: async () => {
      let scope = status.data?.scope ?? null;
      if (status.data?.state === 'UNPREPARED') {
        const prepared = (await attributionInstallationApi.configure(storeId, brandId, 'PREPARE', null)).data;
        scope = prepared.scope;
      }
      if (!scope) throw new Error('SCOPE_MISSING');
      return (await attributionInstallationApi.configure(storeId, brandId, 'ENABLE', scope.installationId)).data;
    },
    onSuccess: data => { cache.setQueryData(key, data); },
  });
  const disable = useMutation({
    mutationFn: async () => {
      const installationId = status.data?.scope?.installationId;
      if (!installationId) throw new Error('SCOPE_MISSING');
      return (await attributionInstallationApi.configure(storeId, brandId, 'DISABLE', installationId)).data;
    },
    onSuccess: data => { cache.setQueryData(key, data); setConfirmDisable(false); },
  });

  const [pendingReceipt, setPendingReceipt] = useState<{ id: string; after?: string } | null>(null);
  const acquire = useMutation({
    mutationFn: async () => {
      if (!status.data?.scope) throw new Error('SCOPE_MISSING');
      let receipt = pendingReceipt;
      if (!receipt) {
        const read = await attributionInstallationApi.acquire(storeId, status.data.scope);
        receipt = { id: read.data.receipt.id };
        setPendingReceipt(receipt);
      }
      const result = await attributionLinkApi.record(storeId, brandId, receipt.id, receipt.after);
      setPendingReceipt(result.data.nextAfter ? { id: receipt.id, after: result.data.nextAfter } : null);
      return { remaining: !!result.data.nextAfter };
    },
  });

  const enabled = status.data?.state === 'ENABLED';
  const busy = enable.isPending || disable.isPending;

  return (
    <div className="bg-surface-hover/30 border border-border-subtle rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <Store className="w-4 h-4 text-accent-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-text-primary text-sm font-medium">{storeName}</span>
          <p className="text-xs text-text-muted mt-0.5">
            {status.isPending && 'Sprawdzanie stanu pomiaru...'}
            {status.isError && 'Nie można odczytać stanu. Sklep wymaga aktualnego Engine i działającej integracji.'}
            {status.data && (enabled
              ? `Pomiar działa. System sam zapisuje wejścia i powiązuje zamówienia raz dziennie.`
              : 'Pomiar wyłączony. Włączenie zajmuje jedno kliknięcie.')}
          </p>
        </div>
        {status.data && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${enabled ? 'bg-success/15 text-success' : 'bg-surface-hover text-text-muted'}`}>
            {enabled ? 'Włączony' : 'Wyłączony'}
          </span>
        )}
        {status.data && (
          <button
            onClick={() => (enabled ? setConfirmDisable(true) : enable.mutate())}
            disabled={busy || (!enabled && !status.data.consentAdapter)}
            className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {enabled ? 'Wyłącz' : 'Włącz pomiar'}
          </button>
        )}
        {status.isError && (
          <button onClick={() => void status.refetch()} className="btn-secondary text-xs">Sprawdź ponownie</button>
        )}
      </div>

      {status.data && !status.data.consentAdapter && (
        <p className="text-xs text-warning">
          Pomiar wymaga banera cookies na stronie sklepu (zgody analityczne i marketingowe).{' '}
          {kreatorHref ? (
            <Link to={kreatorHref} className="underline">Otwórz ustawienia banera w Kreatorze</Link>
          ) : (
            'Najpierw dodaj i włącz baner w Kreatorze.'
          )}
        </p>
      )}
      {(enable.isError || disable.isError) && (
        <p role="alert" className="text-xs text-danger">Nie udało się zmienić ustawienia. Odczytaj aktualny stan i spróbuj ponownie.</p>
      )}

      {status.data?.scope && (
        <details className="text-sm">
          <summary className="cursor-pointer text-text-secondary">Linki kampanii (do reklam Meta i wiadomości e-mail)</summary>
          <div className="mt-3">
            <FinanceAttributionLinkForm storeId={storeId} brandId={brandId} />
          </div>
        </details>
      )}

      {status.data && (
        <details className="text-sm">
          <summary className="cursor-pointer text-text-muted">Diagnostyka</summary>
          <div className="mt-2 space-y-2 text-xs text-text-muted">
            <p>Engine {status.data.engineVersion} · Aktywne linki kampanii: {status.data.linkCount}</p>
            <div className="space-y-1">
              <div>
                <Button variant="secondary" size="sm" disabled={!enabled} loading={acquire.isPending} onClick={() => acquire.mutate()}>
                  {pendingReceipt ? 'Kontynuuj powiązywanie' : 'Powiąż zamówienia natychmiast'}
                </Button>
              </div>
              <p>Wszystkie powiązania wykonują się automatycznie raz dziennie, ok. godz. 3:30. Kliknij przycisk, by powiązać zamówienia natychmiast.</p>
            </div>
            {acquire.isError && <p role="alert" className="text-danger">Nie udało się pobrać wejść. Sprawdź stan pomiaru i integracji.</p>}
            {acquire.isSuccess && (
              <p role="status" className="text-success">
                {acquire.data?.remaining ? 'Część zamówień powiązana. Kontynuuj, aby odebrać następną partię.' : 'Wejścia zapisane, a dostępne zamówienia powiązane.'}
              </p>
            )}
          </div>
        </details>
      )}

      <ConfirmDialog
        isOpen={confirmDisable}
        subtitle={null}
        danger
        title="Wyłączyć pomiar wejść?"
        message="Nowe wejścia przestaną być zapisywane. Zebrane dane i powiązania zostają, a przerwa będzie widoczna w historii pomiaru."
        confirmLabel="Wyłącz"
        isPending={disable.isPending}
        onConfirm={() => disable.mutate()}
        onClose={() => setConfirmDisable(false)}
      />
    </div>
  );
}
